import { jsPDF } from "jspdf";

function phiPct(t, config) {
  if (config.elution_mode !== "gradient") return config.percent_organic ?? 0;
  const g = config.gradient || {};
  const start = g.start_percent ?? 10;
  const end = g.end_percent ?? 90;
  const dur = Math.max(0.1, g.duration_min ?? 10);
  return start + (end - start) * Math.min(1, Math.max(0, t / dur));
}

function drawChromatogram(data, config) {
  const W = 1520, H = 560, pad = 70;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0a0a0a";
  ctx.fillRect(0, 0, W, H);

  const trace = data.trace || [];
  const runtime = data.runtime_min || 10;
  const ymax = Math.max(10, ...trace.map((p) => p.mAU)) * 1.12;
  const ymin = Math.min(0, ...trace.map((p) => p.mAU));
  const xToPx = (t) => pad + (t / runtime) * (W - pad * 2);
  const yToPx = (v) => H - pad - ((v - ymin) / (ymax - ymin)) * (H - pad * 2);

  // grid + axes
  ctx.strokeStyle = "#27272a";
  ctx.lineWidth = 1;
  ctx.font = "20px monospace";
  ctx.fillStyle = "#a1a1aa";
  for (let i = 0; i <= 8; i++) {
    const t = (runtime / 8) * i;
    const x = xToPx(t);
    ctx.beginPath(); ctx.moveTo(x, pad); ctx.lineTo(x, H - pad); ctx.stroke();
    ctx.fillText(t.toFixed(1), x - 12, H - pad + 28);
  }
  ctx.fillText("Waktu (menit)", W / 2 - 60, H - 14);
  ctx.save();
  ctx.translate(20, H / 2 + 20); ctx.rotate(-Math.PI / 2); ctx.fillText("mAU", 0, 0); ctx.restore();

  // gradient overlay
  if (config.elution_mode) {
    ctx.strokeStyle = "#f59e0b";
    ctx.setLineDash([10, 8]);
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i <= 100; i++) {
      const t = (runtime / 100) * i;
      const pct = phiPct(t, config);
      const y = H - pad - (pct / 100) * (H - pad * 2);
      i === 0 ? ctx.moveTo(xToPx(t), y) : ctx.lineTo(xToPx(t), y);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // trace
  ctx.strokeStyle = "#10B981";
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  trace.forEach((p, i) => (i === 0 ? ctx.moveTo(xToPx(p.t), yToPx(p.mAU)) : ctx.lineTo(xToPx(p.t), yToPx(p.mAU))));
  ctx.stroke();

  // peak markers
  ctx.font = "20px monospace";
  (data.peaks || []).forEach((pk) => {
    const x = xToPx(pk.tR);
    ctx.strokeStyle = "#0ea5e9";
    ctx.setLineDash([4, 4]);
    ctx.beginPath(); ctx.moveTo(x, pad); ctx.lineTo(x, H - pad); ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#38bdf8";
    ctx.fillText(pk.name, x + 4, pad + 20);
  });

  return canvas.toDataURL("image/png");
}

export function exportRunPdf({ data, config, colMap = {} }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pw = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = 16;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("Laporan Eksperimen HPLC", margin, y);
  doc.setFontSize(26);
  doc.setTextColor(2, 132, 199);
  doc.text(String(data.score ?? "–"), pw - margin, y, { align: "right" });
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text("Skor", pw - margin, y + 5, { align: "right" });
  doc.setTextColor(0);

  y += 8;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const created = data.created_at ? new Date(data.created_at) : new Date();
  const mode = config.elution_mode === "gradient"
    ? `Gradien ${config.gradient?.start_percent}->${config.gradient?.end_percent}% / ${config.gradient?.duration_min} mnt`
    : `Isokratik ${config.percent_organic}% organik`;
  const lines = [
    `Tanggal: ${created.toLocaleString("id-ID")}`,
    `Kolom: ${colMap[config.column_id] || config.column_id}   |   Mode: ${mode}`,
    `pH ${config.buffer_ph} | Laju ${config.flow_rate_ml_min} mL/mnt | Suhu ${config.temperature_c} C | ${config.wavelength_nm} nm | Inj ${config.injection_volume_ul} uL | Runtime ${data.runtime_min} mnt`,
    `t0 = ${data.t0} mnt   |   N kolom ~ ${data.plate_count}`,
  ];
  lines.forEach((l) => { doc.text(l, margin, y); y += 5; });

  // chromatogram image
  y += 2;
  const img = drawChromatogram(data, config);
  const imgW = pw - margin * 2;
  const imgH = (imgW * 560) / 1520;
  doc.addImage(img, "PNG", margin, y, imgW, imgH);
  y += imgH + 6;

  // metrics table
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text("Hasil Analisis Puncak", margin, y);
  y += 5;
  const cols = ["Analit", "tR", "k'", "N", "Area", "Tinggi", "Tailing", "Rs", "a"];
  const cx = [margin, margin + 42, margin + 60, margin + 76, margin + 100, margin + 124, margin + 148, margin + 168, margin + 182];
  doc.setFontSize(8);
  doc.setFillColor(240, 244, 248);
  doc.rect(margin, y - 4, pw - margin * 2, 6, "F");
  cols.forEach((c, i) => doc.text(c, cx[i], y));
  y += 5;
  doc.setFont("helvetica", "normal");
  (data.peaks || []).forEach((p) => {
    const row = [p.name, p.tR, p.k, p.N, p.area, p.height, p.tailing, p.resolution_prev ?? "-", p.selectivity_prev ?? "-"];
    row.forEach((v, i) => doc.text(String(v), cx[i], y));
    y += 5;
  });
  (data.not_eluted || []).forEach((p) => {
    doc.setTextColor(150);
    doc.text(`${p.name} - tidak terelusi dalam runtime`, margin, y);
    doc.setTextColor(0);
    y += 5;
  });

  // score breakdown
  y += 3;
  const b = data.score_breakdown || {};
  doc.setFont("helvetica", "bold");
  doc.text("Rincian Skor", margin, y); y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(`Resolusi ${b.resolution} | Tailing ${b.tailing} | Efisiensi ${b.efficiency} | Parameter ${b.parameter}`, margin, y);
  y += 7;

  // feedback
  doc.setFont("helvetica", "bold"); doc.setFontSize(10);
  doc.text("Umpan Balik Diagnostik", margin, y); y += 5;
  doc.setFont("helvetica", "normal"); doc.setFontSize(8);
  (data.feedback || []).forEach((f) => {
    const wrapped = doc.splitTextToSize(`- ${f.text}`, pw - margin * 2);
    wrapped.forEach((w) => { if (y > 285) { doc.addPage(); y = 16; } doc.text(w, margin, y); y += 4.5; });
  });

  const stamp = created.toISOString().slice(0, 19).replace(/[:T]/g, "-");
  doc.save(`eksperimen-hplc-${stamp}.pdf`);
}
